import React, { useEffect, useState } from "react";
import { Box, Container, Stack, Typography, Button, CircularProgress } from "@mui/material";
import { Apple, Android } from "@mui/icons-material";
import GoecLogo from "../assets/goec-logo.png";
import StyledDivider from "../ui/styledDivider";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.mercantile.goecm";
const APP_STORE_URL = "https://apps.apple.com/app/id1234567890"; // Placeholder Apple App Store ID

const detectDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "desktop";
  }
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  ) {
    return "ios";
  }

  return "desktop";
};

export default function AppDownload() {
  const [device, setDevice] = useState("desktop");
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const currentDevice = detectDevice();
    setDevice(currentDevice);

    if (currentDevice === "android") {
      setRedirecting(true);
      window.location.replace(PLAY_STORE_URL);
    } else if (currentDevice === "ios") {
      setRedirecting(true);
      window.location.replace(APP_STORE_URL);
    }
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "background.default",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Stack
          sx={{
            backgroundColor: "secondary.main",
            borderRadius: "16px",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.35)",
            overflow: "hidden",
            textAlign: "center",
          }}
          py={4}
          px={3}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              mb: 2,
            }}
          >
            <img
              src={GoecLogo}
              alt="GOEC Logo"
              style={{ width: "160px", maxWidth: "60%", height: "auto", objectFit: "contain" }}
            />
            <StyledDivider />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "white",
              mb: 1,
            }}
          >
            Get GOEC Mobile App
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "primary.DimText",
              mb: 3,
              px: 2,
            }}
          >
            {redirecting
              ? `Redirecting you to the ${device === "android" ? "Google Play Store" : "Apple App Store"}...`
              : "Discover charging stations, start charging sessions, and manage payments effortlessly."}
          </Typography>

          {redirecting && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <CircularProgress size={32} sx={{ color: "primary.light" }} />
            </Box>
          )}

          <Stack spacing={2} sx={{ mt: 2, width: "100%", maxWidth: "340px", mx: "auto" }}>
            {/* Play Store Button */}
            <Button
              component="a"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<Android sx={{ fontSize: 28 }} />}
              sx={{
                backgroundColor: "#000000",
                color: "#FFFFFF",
                border: "1px solid #444",
                borderRadius: "10px",
                py: 1.5,
                px: 3,
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                justifyContent: "flex-start",
                gap: 1.5,
                "&:hover": {
                  backgroundColor: "#1f2937",
                  borderColor: "#666",
                },
              }}
            >
              <Box sx={{ textAlign: "left", lineHeight: 1.2 }}>
                <Typography sx={{ fontSize: "11px", opacity: 0.8, textTransform: "uppercase" }}>
                  Get it on
                </Typography>
                <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                  Google Play
                </Typography>
              </Box>
            </Button>

            {/* App Store Button */}
            <Button
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<Apple sx={{ fontSize: 28 }} />}
              sx={{
                backgroundColor: "#000000",
                color: "#FFFFFF",
                border: "1px solid #444",
                borderRadius: "10px",
                py: 1.5,
                px: 3,
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                justifyContent: "flex-start",
                gap: 1.5,
                "&:hover": {
                  backgroundColor: "#1f2937",
                  borderColor: "#666",
                },
              }}
            >
              <Box sx={{ textAlign: "left", lineHeight: 1.2 }}>
                <Typography sx={{ fontSize: "11px", opacity: 0.8, textTransform: "uppercase" }}>
                  Download on the
                </Typography>
                <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                  App Store
                </Typography>
              </Box>
            </Button>
          </Stack>

          {redirecting && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "primary.DimText",
                mt: 3,
              }}
            >
              If the redirect doesn't happen automatically, tap the button above.
            </Typography>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
